/**
 * @fileoverview ArcMosaico competency disc (AR-792)
 * @description Direct port of the arcmosaico.ai hero disc (disc-generator.js):
 * same seeded PRNG (seed 42), same rings, same domain colors, same 40s spin
 * and glow — so the signup illustration matches the site exactly.
 */

import { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'

const DISC_SEED = 42
const DISC_VIEWBOX = 400
const DISC_CENTER = 200
const OUTER_GAP_DEG = 2.8
const INNER_GAP_DEG = 0.6
const SPIN_DURATION_S = 40

/** Domain colors + fill density from the site generator */
const DOMAINS = [
  { color: '#C32B86', fill: 0.74 }, // The Thinking Mind
  { color: '#D8589F', fill: 0.58 }, // The Thinking Mind
  { color: '#4B2E91', fill: 0.8 }, // The Inner Compass
  { color: '#7B5FB8', fill: 0.52 }, // The Inner Compass
  { color: '#2A6E96', fill: 0.7 }, // The Connected World
  { color: '#5B9BBF', fill: 0.6 }, // The Connected World
]

/** Ring radii/segments of the site hero disc */
const RINGS = [
  { r1: 48, r2: 88, segs: 1 },
  { r1: 90, r2: 125, segs: 2 },
  { r1: 127, r2: 156, segs: 4 },
  { r1: 158, r2: 180, segs: 8 },
  { r1: 182, r2: 197, segs: 14 },
]

const seededRand = (seed: number) => {
  let s = seed
  return () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
}

const polar = (cx: number, cy: number, r: number, deg: number) => {
  const rad = ((deg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

const arcPath = (cx: number, cy: number, r1: number, r2: number, a1: number, a2: number) => {
  const p1 = polar(cx, cy, r1, a1)
  const p2 = polar(cx, cy, r2, a1)
  const p3 = polar(cx, cy, r2, a2)
  const p4 = polar(cx, cy, r1, a2)
  const lg = a2 - a1 > 180 ? 1 : 0
  const f = (n: number) => n.toFixed(2)
  return (
    `M${f(p1.x)},${f(p1.y)}L${f(p2.x)},${f(p2.y)}A${r2},${r2},0,${lg},1,${f(p3.x)},${f(p3.y)}` +
    `L${f(p4.x)},${f(p4.y)}A${r1},${r1},0,${lg},0,${f(p1.x)},${f(p1.y)}Z`
  )
}

interface DiscSegment {
  d: string
  color: string
  opacity: string
}

const buildDisc = (): DiscSegment[] => {
  const rand = seededRand(DISC_SEED)
  const dAngle = 360 / DOMAINS.length
  const segments: DiscSegment[] = []

  DOMAINS.forEach((dom, di) => {
    const dStart = di * dAngle + OUTER_GAP_DEG / 2
    const dEnd = (di + 1) * dAngle - OUTER_GAP_DEG / 2

    RINGS.forEach((ring, ri) => {
      const span = (dEnd - dStart) / ring.segs

      for (let s = 0; s < ring.segs; s++) {
        const a1 = dStart + s * span + (ring.segs > 1 ? INNER_GAP_DEG / 2 : 0)
        const a2 = dStart + (s + 1) * span - (ring.segs > 1 ? INNER_GAP_DEG / 2 : 0)
        const filled = rand() < dom.fill - ri * 0.07

        segments.push({
          d: arcPath(DISC_CENTER, DISC_CENTER, ring.r1, ring.r2, a1, a2),
          color: dom.color,
          opacity: filled ? (0.72 + rand() * 0.28).toFixed(2) : '0.09',
        })
      }
    })
  })

  return segments
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

/** Same wrap/glow/spin as .hero-disc-wrap / #hero-disc on the site */
const DiscWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  /* Glow lives on the static wrap: a filter on the spinning svg itself would
     force a re-rasterization every animation frame */
  &::before {
    content: '';
    position: absolute;
    width: 90%;
    height: 90%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(91, 79, 235, 0.18) 0%, transparent 70%);
  }

  svg {
    width: 100%;
    height: auto;
    position: relative;
    z-index: 1;
    animation: ${spin} ${SPIN_DURATION_S}s linear infinite;
  }
`

export const ArcHeroDisc = () => {
  const segments = useMemo(buildDisc, [])

  return (
    <DiscWrap aria-hidden='true'>
      <svg viewBox={`0 0 ${DISC_VIEWBOX} ${DISC_VIEWBOX}`} xmlns='http://www.w3.org/2000/svg'>
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.d}
            fill={seg.color}
            opacity={seg.opacity}
            stroke='white'
            strokeWidth={1.2}
          />
        ))}
      </svg>
    </DiscWrap>
  )
}
